import { makeAutoObservable } from 'mobx';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { changeAccountContactPreferencesNew } from 'src_server/trpc/types/accountModel';
import { ContactPreferencesNewOptionType } from 'src_server/trpc/types/accountModel/changeAccountContactPreferencesNew';

type OffersContactPreferencesNewType = {
    [type in changeAccountContactPreferencesNew.ContactPreferencesNewOptionType]: boolean;
};
export interface ContactPreferencesNewformatFormModelType {
    newContactPreferences: true;
    contactPreferences: changeAccountContactPreferencesNew.ContactPreferencesNewType[];
}

type ContactPreferencesGroupType = {
    [type in changeAccountContactPreferencesNew.ContactPreferencesNewKeyType]: changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[];
};

export class OffersContactPreferencesOptionsState {
    public betting: FormInputState<boolean, boolean>;
    public casino: FormInputState<boolean, boolean>;
    public bingo: FormInputState<boolean, boolean>;

    public contactPreferencesGroup: FormModel<OffersContactPreferencesNewType>;
    public formatFormModel: FormModel<changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[]>;

    public constructor(private readonly preferences?: ContactPreferencesNewOptionType[]) {
        this.betting = FormInputState.new(this.preferences?.includes('betting') ?? false);
        this.casino = FormInputState.new(this.preferences?.includes('casino') ?? false);
        this.bingo = FormInputState.new(this.preferences?.includes('bingo') ?? false);

        this.contactPreferencesGroup = FormModel.group({
            bingo: this.bingo,
            casino: this.casino,
            betting: this.betting,
        });

        this.formatFormModel = this.contactPreferencesGroup.map(
            (
                obj: OffersContactPreferencesNewType
            ): Result<changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[]> => {
                return Result.createOk(
                    Object.entries(obj).reduce<changeAccountContactPreferencesNew.ContactPreferencesNewOptionType[]>(
                        (prev, [key, value]) => {
                            if (value) {
                                const parseKey =
                                    changeAccountContactPreferencesNew.ContactPreferencesOptionSchema.safeParse(key);
                                if (parseKey.success) {
                                    return [parseKey.data, ...prev];
                                }
                                return prev;
                            }
                            return prev;
                        },
                        []
                    )
                );
            }
        );

        makeAutoObservable(this);
    }
}

export class OffersContactPreferencesNewState {
    public email: OffersContactPreferencesOptionsState;
    public phone: OffersContactPreferencesOptionsState;
    public sms: OffersContactPreferencesOptionsState;

    public contactPreferencesGroup: FormModel<ContactPreferencesGroupType>;
    public formatFormModel: FormModel<ContactPreferencesNewformatFormModelType>;

    public constructor(
        private readonly emailPreferences?: ContactPreferencesNewOptionType[],
        private readonly phonePreferences?: ContactPreferencesNewOptionType[],
        private readonly smsPreferences?: ContactPreferencesNewOptionType[]
    ) {
        this.email = new OffersContactPreferencesOptionsState(this.emailPreferences);
        this.phone = new OffersContactPreferencesOptionsState(this.phonePreferences);
        this.sms = new OffersContactPreferencesOptionsState(this.smsPreferences);

        this.contactPreferencesGroup = FormModel.group({
            email: this.email.formatFormModel,
            sms: this.sms.formatFormModel,
            phone: this.phone.formatFormModel,
        });

        this.formatFormModel = this.contactPreferencesGroup.map(
            (obj): Result<ContactPreferencesNewformatFormModelType> => {
                const result = Object.entries(obj)
                    .reduce<changeAccountContactPreferencesNew.ContactPreferencesNewType[]>((prev, [key, value]) => {
                        const parseResult =
                            changeAccountContactPreferencesNew.ContactPreferencesKeySchema.safeParse(key);
                        if (parseResult.success) {
                            const contactPreference: changeAccountContactPreferencesNew.ContactPreferencesNewType = {
                                type: parseResult.data,
                                categories: value,
                            };
                            return [contactPreference, ...prev];
                        }

                        return prev;
                    }, [])
                    .filter((item) => item.categories.length > 0);

                return Result.createOk({
                    newContactPreferences: true,
                    contactPreferences: result,
                });
            }
        );

        makeAutoObservable(this);
    }
}
